import { useState } from "react";

import { CAREER_GOAL_OPTIONS, submitGoalsOnboarding } from "@/api/onboarding";
import { Button } from "@/components/ui/button";

type Props = {
  initialGoals?: string[];
  onBack?: () => void;
  onDone: () => Promise<void> | void;
};

/** Step 3: what the artist wants from ArtistNav. Context only. */
export default function GoalsStep({ initialGoals = [], onBack, onDone }: Props) {
  const [selected, setSelected] = useState<string[]>(initialGoals);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((goal) => goal !== id) : [...prev, id]));

  const handleContinue = async () => {
    setSaving(true);
    setError(null);
    try {
      await submitGoalsOnboarding(selected);
      await onDone();
    } catch {
      setError("We couldn't save your goals. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="space-y-4" aria-label="Goals">
      <div>
        <h2 className="text-xl font-semibold text-foreground">What do you want to accomplish?</h2>
        <p className="text-sm text-muted-foreground">Pick as many as you like.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {CAREER_GOAL_OPTIONS.map((goal) => {
          const active = selected.includes(goal.id);
          return (
            <button
              key={goal.id}
              type="button"
              onClick={() => toggle(goal.id)}
              aria-pressed={active}
              className={`rounded-full border px-4 py-2 text-sm transition ${
                active ? "border-primary bg-primary/15 text-foreground" : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {goal.label}
            </button>
          );
        })}
      </div>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex items-center justify-between">
        {onBack ? (
          <Button type="button" variant="ghost" onClick={onBack} disabled={saving}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Button onClick={() => void handleContinue()} disabled={saving}>
          {saving ? "Saving…" : "Continue"}
        </Button>
      </div>
    </section>
  );
}
